import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { HealthRing } from "../components/HealthRing";
import { LogoMark } from "../components/LogoMark";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Hammer, Paintbrush, HeartPulse, ShieldCheck, Globe2, ArrowRight, ClipboardCheck,
  FileText, Camera, CreditCard, Wrench, MapPin, Phone, Mail, Menu, X, CheckCircle2, Leaf, Loader2,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  build: "https://images.unsplash.com/photo-1787672358208-94075d85ab7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  transform: "https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  care: "https://images.unsplash.com/photo-1730660338791-8d63548efc75?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  diaspora: "https://images.unsplash.com/photo-1698994705178-d244d73ea573?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  city: "https://images.unsplash.com/photo-1594386479412-fa62932f4cdc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const T = {
  en: {
    nav: { services: "Services", diaspora: "Diaspora", how: "How it works", coverage: "Coverage", contact: "Contact" },
    signin: "Sign in", getstarted: "Get started", dashboard: "Go to Dashboard",
    badge: "Digital Property Management & Maintenance — Cameroon",
    heroA: "Your Property.", heroB: "Our Responsibility.",
    heroSub: "Build, renovate and maintain your Cameroon property with full transparency — inspections, quotes, site photos and payments, all in one platform. Perfect for owners at home and the diaspora abroad.",
    heroCta1: "Register your property", heroCta2: "Explore services",
    ticks: ["Permanent property ID", "Live site photos", "Pay from anywhere"],
    trust: [["Property Health Score", "Every home rated out of 100"], ["Diaspora-first", "Manage from anywhere in the world"], ["FCFA transparency", "Itemised quotes, no surprises"], ["Local teams", "Trusted crews on the ground"]],
    servTag: "What we do", servTitle: "Three ways we look after your property",
    servSub: "From an empty plot to lifelong upkeep, choose the service that fits where your property is today.",
    products: [
      ["For unfinished buildings", ["Site survey & Bill of Quantities", "Transparent construction quotation", "Managed build to completion"]],
      ["Renovation & interior", ["Full property inspection", "Renovation & interior design", "Landscaping & finishing"]],
      ["Recurring management", ["Monthly inspections & reports", "Garden & minor repairs", "One monthly fee, full peace of mind"]],
    ],
    diTag: "Diaspora Property Care", diTitle: "Own in Cameroon. Live anywhere.",
    diSub: "Living in Berlin, Paris or Washington? Open the app and see exactly what's happening with your property in Kumba — health score, open work, photos and invoices. Approve a quote and pay online, without ever booking a flight.",
    diPoints: ["See your property's health score update after each inspection", "Approve quotations and pay invoices from abroad", "Receive site photos at every stage of the work", "One trusted team accountable for your home"],
    diCta: "Start managing remotely",
    howTag: "How it works", howTitle: "From first survey to lifelong care",
    steps: [
      ["Register your property", "Get a permanent property ID that follows your home for life."],
      ["Request an inspection", "Our local team surveys and scores your property's health."],
      ["Receive a BOQ & quote", "Clear, itemised pricing in FCFA — no hidden costs."],
      ["Track the work", "Follow before / during / after site photos from anywhere."],
      ["Pay securely online", "Settle invoices by card or mobile money, wherever you live."],
      ["Ongoing maintenance", "Keep your property cared for with a CARE contract."],
    ],
    covTag: "Coverage & rollout", covTitle: "Growing across Cameroon, city by city",
    covSub: "We're starting where we know best and expanding to the country's major hubs.",
    cities: [["Kumba", "Live now"], ["Buea", "Next"], ["Douala", "Coming soon"], ["Yaoundé", "Coming soon"]],
    hsTag: "Property Health Score", hsTitle: "One number that tells the whole story",
    hsSub: "After every inspection we score structure, roof, electrical, plumbing, painting, garden and security — then rank the top priorities so you always know what to fix first.",
    ctaTitle: "Ready to protect your property?", ctaSub: "Register today and get your first inspection scheduled with our local Kumba team.",
    ctaCall: "Call us",
    formTag: "Request a callback", formTitle: "Let's look after your property",
    formSub: "Leave your details and our Kumba team will call you back.",
    fName: "Full name", fPhone: "Phone", fEmail: "Email (optional)", fCity: "City", fMsg: "How can we help?",
    fSubmit: "Request a callback", fOk: "Thank you! Our team will call you back shortly.",
    footerTag: "Digital property management & maintenance for Cameroon and the diaspora.",
    rights: "All rights reserved.",
  },
  fr: {
    nav: { services: "Services", diaspora: "Diaspora", how: "Comment ça marche", coverage: "Couverture", contact: "Contact" },
    signin: "Se connecter", getstarted: "Commencer", dashboard: "Tableau de bord",
    badge: "Gestion & Entretien Immobilier Numérique — Cameroun",
    heroA: "Votre Bien.", heroB: "Notre Responsabilité.",
    heroSub: "Construisez, rénovez et entretenez votre bien au Cameroun en toute transparence — inspections, devis, photos de chantier et paiements, sur une seule plateforme. Idéal pour les propriétaires sur place et la diaspora.",
    heroCta1: "Enregistrer mon bien", heroCta2: "Voir les services",
    ticks: ["Identifiant permanent", "Photos de chantier en direct", "Payez de partout"],
    trust: [["Score de Santé du Bien", "Chaque bien noté sur 100"], ["Priorité diaspora", "Gérez depuis n'importe où"], ["Transparence FCFA", "Devis détaillés, sans surprise"], ["Équipes locales", "Des équipes de confiance sur place"]],
    servTag: "Nos services", servTitle: "Trois façons de prendre soin de votre bien",
    servSub: "Du terrain nu à l'entretien à vie, choisissez le service adapté à l'état actuel de votre bien.",
    products: [
      ["Pour bâtiments inachevés", ["Relevé & devis quantitatif (BOQ)", "Devis de construction transparent", "Construction gérée jusqu'à la fin"]],
      ["Rénovation & intérieur", ["Inspection complète du bien", "Rénovation & design intérieur", "Aménagement paysager & finitions"]],
      ["Gestion récurrente", ["Inspections & rapports mensuels", "Jardin & petites réparations", "Un forfait mensuel, tranquillité totale"]],
    ],
    diTag: "Diaspora Property Care", diTitle: "Propriétaire au Cameroun. Où que vous viviez.",
    diSub: "À Berlin, Paris ou Washington ? Ouvrez l'application et suivez exactement ce qui se passe pour votre bien à Kumba — score de santé, travaux, photos et factures. Approuvez un devis et payez en ligne, sans prendre l'avion.",
    diPoints: ["Suivez le score de santé après chaque inspection", "Approuvez les devis et payez les factures depuis l'étranger", "Recevez des photos à chaque étape des travaux", "Une équipe de confiance responsable de votre bien"],
    diCta: "Gérer à distance",
    howTag: "Comment ça marche", howTitle: "Du premier relevé à l'entretien à vie",
    steps: [
      ["Enregistrez votre bien", "Obtenez un identifiant permanent qui suit votre bien à vie."],
      ["Demandez une inspection", "Notre équipe locale évalue et note la santé de votre bien."],
      ["Recevez un BOQ & devis", "Tarifs clairs et détaillés en FCFA — sans frais cachés."],
      ["Suivez les travaux", "Photos avant / pendant / après depuis n'importe où."],
      ["Payez en ligne", "Réglez par carte ou mobile money, où que vous soyez."],
      ["Entretien continu", "Gardez votre bien entretenu avec un contrat CARE."],
    ],
    covTag: "Couverture & déploiement", covTitle: "Nous grandissons au Cameroun, ville par ville",
    covSub: "Nous commençons là où nous excellons et nous étendons aux grandes villes du pays.",
    cities: [["Kumba", "Disponible"], ["Buea", "Prochainement"], ["Douala", "Bientôt"], ["Yaoundé", "Bientôt"]],
    hsTag: "Score de Santé du Bien", hsTitle: "Un seul chiffre qui dit tout",
    hsSub: "Après chaque inspection, nous notons structure, toiture, électricité, plomberie, peinture, jardin et sécurité — puis nous classons les priorités pour savoir quoi réparer d'abord.",
    ctaTitle: "Prêt à protéger votre bien ?", ctaSub: "Inscrivez-vous aujourd'hui et planifiez votre première inspection avec notre équipe de Kumba.",
    ctaCall: "Appelez-nous",
    formTag: "Demander un rappel", formTitle: "Prenons soin de votre bien",
    formSub: "Laissez vos coordonnées et notre équipe de Kumba vous rappellera.",
    fName: "Nom complet", fPhone: "Téléphone", fEmail: "Email (optionnel)", fCity: "Ville", fMsg: "Comment pouvons-nous aider ?",
    fSubmit: "Demander un rappel", fOk: "Merci ! Notre équipe vous rappellera très vite.",
    footerTag: "Gestion et entretien immobilier numérique pour le Cameroun et la diaspora.",
    rights: "Tous droits réservés.",
  },
};

const PRODUCT_META = [
  { icon: Hammer, name: "Walo BUILD", color: "hsl(197 37% 40%)", img: IMG.build },
  { icon: Paintbrush, name: "Walo TRANSFORM", color: "hsl(15 75% 62%)", img: IMG.transform },
  { icon: HeartPulse, name: "Walo CARE", color: "hsl(142 39% 30%)", img: IMG.care },
];
const TRUST_ICONS = [HeartPulse, Globe2, ShieldCheck, Leaf];
const STEP_ICONS = [ClipboardCheck, ClipboardCheck, FileText, Camera, CreditCard, Wrench];

function CallbackForm({ t }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, email: form.email || null });
      setDone(true);
      toast.success(t.fOk);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setLoading(false); }
  };
  if (done) return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center" data-testid="lead-success">
      <CheckCircle2 className="h-12 w-12 text-accent mx-auto mb-4" />
      <p className="text-white font-heading text-xl font-bold">{t.fOk}</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4" data-testid="callback-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className="text-white/80">{t.fName}</Label>
          <Input required value={form.name} onChange={set("name")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="lead-name" /></div>
        <div className="space-y-1.5"><Label className="text-white/80">{t.fPhone}</Label>
          <Input required value={form.phone} onChange={set("phone")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="lead-phone" /></div>
        <div className="space-y-1.5"><Label className="text-white/80">{t.fEmail}</Label>
          <Input type="email" value={form.email} onChange={set("email")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="lead-email" /></div>
        <div className="space-y-1.5"><Label className="text-white/80">{t.fCity}</Label>
          <Input value={form.city} onChange={set("city")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="lead-city" /></div>
      </div>
      <div className="space-y-1.5"><Label className="text-white/80">{t.fMsg}</Label>
        <Textarea rows={3} value={form.message} onChange={set("message")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="lead-message" /></div>
      <Button type="submit" disabled={loading} className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-11" data-testid="lead-submit">
        {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t.fSubmit}
      </Button>
    </form>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <LogoMark size={40} />
      <div className="leading-tight">
        <div className="font-heading font-bold text-lg tracking-tight text-white">NUSTAMAC<span className="text-accent">/</span>WALO</div>
        <div className="text-[0.55rem] uppercase tracking-[0.25em] text-white/60">Property Care</div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [lang, setLang] = useState(() => localStorage.getItem("walo_lang") || "en");
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const t = T[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const toggleLang = () => { const n = lang === "en" ? "fr" : "en"; setLang(n); localStorage.setItem("walo_lang", n); };

  const nav = [["services", t.nav.services], ["diaspora", t.nav.diaspora], ["how", t.nav.how], ["coverage", t.nav.coverage], ["contact", t.nav.contact]];
  const LangBtn = ({ mobile }) => (
    <button onClick={toggleLang} data-testid="lang-toggle"
      className={`flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-xs font-semibold ${mobile ? "text-white" : "text-white/90"} hover:bg-white/10 transition-colors`}>
      <Globe2 className="h-3.5 w-3.5" /> {lang === "en" ? "FR" : "EN"}
    </button>
  );

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-[hsl(142,45%,9%)]/95 backdrop-blur border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden lg:flex items-center gap-8">
            {nav.map(([id, label]) => <a key={id} href={`#${id}`} className="text-sm text-white/80 hover:text-white transition-colors">{label}</a>)}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <LangBtn />
            {user ? (
              <Link to="/dashboard"><Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="nav-dashboard-btn">{t.dashboard}</Button></Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-white/90 hover:text-white" data-testid="nav-signin-link">{t.signin}</Link>
                <Link to="/register"><Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="nav-getstarted-btn">{t.getstarted}</Button></Link>
              </>
            )}
          </div>
          <div className="lg:hidden flex items-center gap-2">
            <LangBtn mobile />
            <button className="text-white" onClick={() => setMenu(!menu)} data-testid="landing-menu-toggle">{menu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
          </div>
        </div>
        {menu && (
          <div className="lg:hidden bg-[hsl(142,45%,9%)] border-t border-white/10 px-5 py-4 flex flex-col gap-3">
            {nav.map(([id, label]) => <a key={id} href={`#${id}`} onClick={() => setMenu(false)} className="text-white/85 py-1">{label}</a>)}
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full border-white/30 text-white bg-transparent">{t.signin}</Button></Link>
              <Link to={user ? "/dashboard" : "/register"} className="flex-1"><Button className="w-full rounded-full bg-accent text-accent-foreground">{user ? t.dashboard : t.getstarted}</Button></Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={IMG.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, hsl(142 45% 8% / 0.95) 0%, hsl(142 45% 10% / 0.82) 45%, hsl(142 45% 12% / 0.35) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-16 w-full">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs text-white/90 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t.badge}
            </span>
            <h1 className="font-heading text-white font-bold tracking-tight leading-[1.05] mt-6 text-4xl sm:text-5xl lg:text-6xl">
              {t.heroA}<br /><span className="text-accent">{t.heroB}</span>
            </h1>
            <p className="text-white/80 text-lg mt-6 max-w-xl">{t.heroSub}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register"><Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-7" data-testid="hero-register-btn">{t.heroCta1} <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <a href="#services"><Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-white/30 text-white bg-white/5 hover:bg-white/15" data-testid="hero-services-btn">{t.heroCta2}</Button></a>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-10 text-white/70 text-sm">
              {t.ticks.map((tk) => <span key={tk} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> {tk}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-[hsl(142,45%,9%)] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.trust.map(([label, desc], i) => {
            const Icon = TRUST_ICONS[i];
            return (
              <div key={label} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-accent" /></div>
                <div><p className="text-white font-medium text-sm">{label}</p><p className="text-white/60 text-xs mt-0.5">{desc}</p></div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 scroll-mt-16">
        <p className="overline text-accent">{t.servTag}</p>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-2">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight max-w-lg">{t.servTitle}</h2>
          <p className="text-muted-foreground max-w-sm">{t.servSub}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PRODUCT_META.map((p, i) => (
            <div key={p.name} className="group rounded-2xl border border-border bg-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow]" data-testid={`service-${p.name.split(" ")[1]}`}>
              <div className="h-44 overflow-hidden relative">
                <img src={p.img} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: p.color }}><p.icon className="h-5 w-5 text-white" /></div>
              </div>
              <div className="p-6">
                <p className="overline" style={{ color: p.color }}>{t.products[i][0]}</p>
                <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">{p.name}</h3>
                <ul className="mt-4 space-y-2">
                  {t.products[i][1].map((pt) => <li key={pt} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: p.color }} /> {pt}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Diaspora */}
      <section id="diaspora" className="bg-[hsl(142,45%,9%)] scroll-mt-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="overline text-accent">{t.diTag}</p>
            <h2 className="font-heading text-white text-3xl md:text-4xl font-bold tracking-tight mt-2">{t.diTitle}</h2>
            <p className="text-white/70 mt-4 max-w-lg">{t.diSub}</p>
            <ul className="mt-6 space-y-3">
              {t.diPoints.map((p) => <li key={p} className="flex items-start gap-3 text-white/85"><CheckCircle2 className="h-5 w-5 text-accent mt-0.5 shrink-0" /> {p}</li>)}
            </ul>
            <Link to="/register"><Button size="lg" className="rounded-full mt-8 bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="diaspora-cta">{t.diCta} <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          </div>
          <div className="relative">
            <img src={IMG.diaspora} alt="" className="rounded-2xl w-full h-72 lg:h-96 object-cover" />
            <div className="absolute -bottom-8 -left-2 sm:left-6 bg-card rounded-2xl shadow-2xl p-5 w-64 border border-border">
              <p className="overline text-muted-foreground">My Property · Kumba</p>
              <div className="flex items-center gap-4 mt-3">
                <HealthRing score={82} size={72} stroke={8} />
                <div className="text-sm"><p className="font-medium">Okon Residence</p><p className="text-muted-foreground text-xs font-mono">NW-KUM-000001</p>
                  <span className="inline-flex items-center gap-1 mt-1 text-xs text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> CARE active</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 scroll-mt-16">
        <p className="overline text-accent">{t.howTag}</p>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-2 max-w-lg">{t.howTitle}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {t.steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={s[0]} className="relative rounded-2xl border border-border bg-card p-6 hover:shadow-lg transition-shadow" data-testid={`step-${i + 1}`}>
                <span className="absolute top-5 right-5 font-heading text-4xl font-bold text-secondary">{String(i + 1).padStart(2, "0")}</span>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" /></div>
                <h3 className="font-heading text-lg font-bold tracking-tight mt-4">{s[0]}</h3>
                <p className="text-sm text-muted-foreground mt-1">{s[1]}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Coverage */}
      <section id="coverage" className="relative scroll-mt-16">
        <img src={IMG.city} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[hsl(142,45%,9%)]/92" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="overline text-accent">{t.covTag}</p>
          <h2 className="font-heading text-white text-3xl md:text-4xl font-bold tracking-tight mt-2 max-w-xl">{t.covTitle}</h2>
          <p className="text-white/70 mt-3 max-w-lg">{t.covSub}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {t.cities.map(([name, status], i) => {
              const active = i === 0;
              return (
                <div key={name} className={`rounded-2xl p-6 border ${active ? "bg-accent/15 border-accent/40" : "bg-white/5 border-white/10"}`} data-testid={`city-${name}`}>
                  <div className="flex items-center gap-2 text-white"><MapPin className={`h-5 w-5 ${active ? "text-accent" : "text-white/50"}`} /><span className="font-heading text-xl font-bold">{name}</span></div>
                  <p className={`text-sm mt-2 ${active ? "text-accent" : "text-white/50"}`}>{status}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Health */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="flex justify-center"><div className="relative"><HealthRing score={82} size={200} stroke={16} /><p className="text-center overline text-muted-foreground mt-3">{t.hsTag}</p></div></div>
        <div>
          <p className="overline text-accent">{t.hsTag}</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-2">{t.hsTitle}</h2>
          <p className="text-muted-foreground mt-4 max-w-lg">{t.hsSub}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-16 md:py-20 text-center">
          <h2 className="font-heading text-primary-foreground text-3xl md:text-4xl font-bold tracking-tight">{t.ctaTitle}</h2>
          <p className="text-primary-foreground/80 mt-4 max-w-xl mx-auto">{t.ctaSub}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/register"><Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-8" data-testid="cta-register-btn">{t.heroCta1}</Button></Link>
            <a href="tel:+237685829774"><Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-white/40 text-white bg-transparent hover:bg-white/10" data-testid="cta-call-btn"><Phone className="h-4 w-4 mr-2" /> {t.ctaCall}</Button></a>
          </div>
        </div>
      </section>

      {/* Contact + callback form */}
      <section id="contact" className="bg-[hsl(142,45%,9%)] scroll-mt-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="overline text-accent">{t.formTag}</p>
            <h2 className="font-heading text-white text-3xl md:text-4xl font-bold tracking-tight mt-2">{t.formTitle}</h2>
            <p className="text-white/70 mt-3 max-w-md">{t.formSub}</p>
            <ul className="mt-8 space-y-4 text-white/85">
              <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-accent mt-0.5 shrink-0" /> Buea Road, Kumba, Cameroon</li>
              <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-accent shrink-0" /> <a href="tel:+237685829774" className="hover:text-white">+237 685 829 774</a></li>
              <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-accent shrink-0" /> hello@nustamacwalo.cm</li>
            </ul>
          </div>
          <CallbackForm t={t} />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[hsl(142,45%,8%)] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div><Brand /><p className="text-white/60 text-sm mt-3 max-w-xs">{t.footerTag}</p></div>
          <div className="text-white/50 text-xs">
            <p>© {new Date().getFullYear()} NUSTAMAC/WALO. {t.rights}</p>
            <p className="mt-1 sm:text-right">{t.heroA} {t.heroB}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
